import type { I18n } from '@learnforge/i18n';
import type { Interval } from '../func';
import { createAxes, type AxesHandle } from './axes';
import { createSvgEl } from '@learnforge/demo-render';

export interface FunctionGraphCurve {
  sample: Float64Array;
  className: string;
  label?: string;
  /** Mathematical x-values where only this curve's path must break. */
  discontinuities?: readonly number[];
  /** Minimum screen-space separation from an asymptote without changing sampled values. */
  asymptoteClearances?: readonly FunctionGraphCurveClearance[];
  /** Clip the continuous path at these bounds, retaining segments between samples. */
  clipBounds?: { xDomain: [number, number]; yDomain: [number, number] };
}

export interface FunctionGraphCurveClearance {
  kind: 'vertical' | 'horizontal';
  value: number;
  side: 'negative' | 'positive';
  pixels: number;
}

export interface FunctionGraphAsymptote {
  kind: 'vertical' | 'horizontal';
  value: number;
}

export interface FunctionGraphFeature {
  x: number;
  y: number;
  labelKey: string;
  className: string;
}

export interface FunctionGraphHandle {
  group: SVGGElement;
  update(opts: Partial<FunctionGraphOptions>): void;
  destroy(): void;
}

export interface FunctionGraphOptions {
  xDomain: [number, number];
  yDomain: [number, number];
  width: number;
  height: number;
  curves: readonly FunctionGraphCurve[];
  /** Mathematical x-values where curve paths must break, independent of visible guides. */
  discontinuities?: readonly number[];
  asymptotes?: readonly FunctionGraphAsymptote[];
  features?: readonly FunctionGraphFeature[];
  shadedDomain?: readonly Interval[];
  shadedRange?: readonly Interval[];
  showYxLine?: boolean;
  i18n: I18n<string>;
}

function findLabelAnchor(
  sample: Float64Array,
  xDomain: [number, number],
  yDomain: [number, number],
  clipBounds?: FunctionGraphCurve['clipBounds'],
): { x: number; y: number } | null {
  const [xMin, xMax] = xDomain;
  const [yMin, yMax] = yDomain;
  const n = sample.length;
  for (let i = n - 1; i >= 0; i--) {
    const y = sample[i] as number;
    if (!Number.isFinite(y)) continue;
    if (y < yMin || y > yMax) continue;
    const x = xMin + ((xMax - xMin) * i) / (n - 1);
    if (
      clipBounds &&
      (x < clipBounds.xDomain[0] ||
        x > clipBounds.xDomain[1] ||
        y < clipBounds.yDomain[0] ||
        y > clipBounds.yDomain[1])
    )
      continue;
    return { x, y };
  }
  return null;
}

function buildPath(
  sample: Float64Array,
  xDomain: [number, number],
  toX: (x: number) => number,
  toY: (y: number) => number,
  verticalAsymptotes: readonly number[],
  clearances: readonly FunctionGraphCurveClearance[],
): string {
  const [xMin, xMax] = xDomain;
  const n = sample.length;
  if (n === 0) return '';
  let d = '';
  let pendingMove = true;
  let previousX: number | null = null;
  for (let i = 0; i < n; i++) {
    const xVal = xMin + ((xMax - xMin) * i) / (n - 1);
    const yVal = sample[i] as number;
    if (!Number.isFinite(yVal)) {
      pendingMove = true;
      previousX = xVal;
      continue;
    }
    const segmentStartX = previousX;
    if (
      segmentStartX !== null &&
      verticalAsymptotes.some(
        (asymptote) =>
          asymptote > Math.min(segmentStartX, xVal) && asymptote <= Math.max(segmentStartX, xVal),
      )
    ) {
      pendingMove = true;
    }
    let sx = toX(xVal);
    let sy = toY(yVal);
    for (const clearance of clearances) {
      if (clearance.kind === 'vertical') {
        const asymptoteX = toX(clearance.value);
        sx =
          clearance.side === 'positive'
            ? Math.max(sx, asymptoteX + clearance.pixels)
            : Math.min(sx, asymptoteX - clearance.pixels);
      } else {
        const asymptoteY = toY(clearance.value);
        sy =
          clearance.side === 'positive'
            ? Math.min(sy, asymptoteY - clearance.pixels)
            : Math.max(sy, asymptoteY + clearance.pixels);
      }
    }
    if (pendingMove) {
      d += `M ${sx.toFixed(2)} ${sy.toFixed(2)} `;
      pendingMove = false;
    } else {
      d += `L ${sx.toFixed(2)} ${sy.toFixed(2)} `;
    }
    previousX = xVal;
  }
  return d.trim();
}

let nextGraphId = 0;

export function createFunctionGraph(
  parent: SVGGElement,
  opts: FunctionGraphOptions,
): FunctionGraphHandle {
  const root = createSvgEl('g', { class: 'fn-function-graph' });
  parent.appendChild(root);
  const graphId = nextGraphId++;

  let state: FunctionGraphOptions = { ...opts };

  function render(): void {
    root.replaceChildren();
    const axes: AxesHandle = createAxes(root, {
      xDomain: state.xDomain,
      yDomain: state.yDomain,
      width: state.width,
      height: state.height,
      showGrid: true,
    });

    if (state.shadedRange) {
      for (const iv of state.shadedRange) {
        const sy = axes.toScreenY(iv.end);
        const ey = axes.toScreenY(iv.start);
        const rect = createSvgEl('rect', {
          x: 0,
          y: Math.min(sy, ey),
          width: state.width,
          height: Math.abs(ey - sy),
          class: 'fn-shaded-range',
        });
        root.appendChild(rect);
      }
    }

    if (state.shadedDomain) {
      for (const iv of state.shadedDomain) {
        const sx = axes.toScreenX(iv.start);
        const ex = axes.toScreenX(iv.end);
        const rect = createSvgEl('rect', {
          x: sx,
          y: 0,
          width: ex - sx,
          height: state.height,
          class: 'fn-shaded-domain',
        });
        root.appendChild(rect);
      }
    }

    if (state.asymptotes) {
      for (const a of state.asymptotes) {
        if (a.kind === 'vertical') {
          const sx = axes.toScreenX(a.value);
          root.appendChild(
            createSvgEl('line', {
              x1: sx,
              y1: 0,
              x2: sx,
              y2: state.height,
              class: 'fn-asymptote-vertical',
              'stroke-dasharray': '6 4',
            }),
          );
        } else {
          const sy = axes.toScreenY(a.value);
          root.appendChild(
            createSvgEl('line', {
              x1: 0,
              y1: sy,
              x2: state.width,
              y2: sy,
              class: 'fn-asymptote-horizontal',
              'stroke-dasharray': '6 4',
            }),
          );
        }
      }
    }

    if (state.showYxLine) {
      const lo = Math.max(state.xDomain[0], state.yDomain[0]);
      const hi = Math.min(state.xDomain[1], state.yDomain[1]);
      root.appendChild(
        createSvgEl('line', {
          x1: axes.toScreenX(lo),
          y1: axes.toScreenY(lo),
          x2: axes.toScreenX(hi),
          y2: axes.toScreenY(hi),
          class: 'fn-yx-line',
        }),
      );
    }

    for (const [curveIndex, c] of state.curves.entries()) {
      const verticalDiscontinuities =
        c.discontinuities ??
        state.discontinuities ??
        (state.asymptotes ?? [])
          .filter((asymptote) => asymptote.kind === 'vertical')
          .map((asymptote) => asymptote.value);
      const path = createSvgEl('path', {
        d: buildPath(
          c.sample,
          state.xDomain,
          (x: number) => axes.toScreenX(x),
          (y: number) => axes.toScreenY(y),
          verticalDiscontinuities,
          c.asymptoteClearances ?? [],
        ),
        class: c.className,
        fill: 'none',
      });
      if (c.clipBounds) {
        const clipId = `fn-curve-clip-${graphId}-${curveIndex}`;
        const clip = createSvgEl('clipPath', { id: clipId, clipPathUnits: 'userSpaceOnUse' });
        const left = Math.max(state.xDomain[0], c.clipBounds.xDomain[0]);
        const right = Math.min(state.xDomain[1], c.clipBounds.xDomain[1]);
        const bottom = Math.max(state.yDomain[0], c.clipBounds.yDomain[0]);
        const top = Math.min(state.yDomain[1], c.clipBounds.yDomain[1]);
        clip.appendChild(
          createSvgEl('rect', {
            x: axes.toScreenX(left),
            y: axes.toScreenY(top),
            width: Math.max(0, axes.toScreenX(right) - axes.toScreenX(left)),
            height: Math.max(0, axes.toScreenY(bottom) - axes.toScreenY(top)),
          }),
        );
        const defs = createSvgEl('defs');
        defs.appendChild(clip);
        root.appendChild(defs);
        path.setAttribute('clip-path', `url(#${clipId})`);
      }
      root.appendChild(path);

      if (c.label) {
        const anchor = findLabelAnchor(c.sample, state.xDomain, state.yDomain, c.clipBounds);
        if (anchor) {
          const sx = axes.toScreenX(anchor.x);
          const sy = axes.toScreenY(anchor.y);
          const labelClass = c.className.replace('fn-curve-', 'fn-curve-label-');
          const labelGroup = createSvgEl('g', { class: `fn-curve-label-group ${labelClass}` });
          const anchorEnd = sx > state.width - 80;
          const tx = anchorEnd
            ? Math.min(sx - 6, state.width - 4)
            : Math.min(sx + 8, state.width - 4);
          const ty = Math.max(18, sy - 8);
          const pad = 4;
          const approxWidth = c.label.length * 7 + pad * 2;
          const rectX = anchorEnd ? tx - approxWidth + pad : tx - pad;
          const rect = createSvgEl('rect', {
            x: rectX,
            y: ty - 12,
            width: approxWidth,
            height: 16,
            rx: 3,
            class: 'fn-curve-label-bg',
          });
          labelGroup.appendChild(rect);
          const text = createSvgEl('text', {
            x: tx,
            y: ty,
            class: `fn-curve-label ${labelClass}`,
            'text-anchor': anchorEnd ? 'end' : 'start',
          });
          text.textContent = c.label;
          labelGroup.appendChild(text);
          root.appendChild(labelGroup);
        }
      }
    }

    if (state.features) {
      for (const f of state.features) {
        const sx = axes.toScreenX(f.x);
        const sy = axes.toScreenY(f.y);
        const circle = createSvgEl('circle', {
          cx: sx,
          cy: sy,
          r: 5,
          class: f.className,
        });
        root.appendChild(circle);
        const label = createSvgEl('text', {
          x: sx + 8,
          y: sy - 8,
          class: 'fn-feature-label',
        });
        label.textContent = state.i18n.t(f.labelKey);
        root.appendChild(label);
      }
    }
  }

  render();

  return {
    group: parent,
    update(partial) {
      state = { ...state, ...partial };
      render();
    },
    destroy() {
      root.remove();
    },
  };
}
